<template>
  <div>
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <h1 class="title">Storage Items</h1>
      <app-cells>
        <app-button @click.native="onShowModal(null)">Create</app-button>
        <app-input
          @input.native="onItemSearch"
          v-model="filter_search"
          class="input--300"
          type="search"
          placeholder="Search"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_section"
          :options="section_options"
          placeholder="Section"
          class="select--300"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_type"
          :options="types_options"
          placeholder="Type"
          class="select--300"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #name="{ row }">
          <router-link
            :to="{name: 'storage-items-detail', params: {id: row.id }}"
            class="table-default__link-border"
          >
            {{ row.name }}
          </router-link>
        </template>
        <template #item_type="{ row }">{{ row.item_type?.name || '-' }}</template>
        <template #section="{ row }">{{ row.section.name }}</template>
        <template #category="{ row }">{{ row.category?.name || '-' }}</template>
        <template #measurement="{ row }">{{ row.measurement.name }}</template>
        <template #weight="{ row }">{{ row.weight || '-' }}</template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            @input="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" :limit="40"/>
      </app-cells>
    </template>
    <modal
      name="item_modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('item_modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Create</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Section" required>
              <app-select
                @input="onChangeSection"
                v-model="form.section"
                :reduce="item => item.id"
                :options="section_options"
                label="name"
                placeholder="Section"
                :error="$v.form.section.$error"
              />
              <template #error>
                <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group v-if="!item_to_change" label="Type" required>
              <app-select
                v-model="form.item_type"
                :reduce="item => item.code"
                :options="types_options"
                label="name"
                placeholder="Type"
                :error="$v.form.item_type.$error"
              />
              <template #error>
                <div v-if="$v.form.item.$dirty && !$v.form.item.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Category">
              <app-select
                v-model="form.category"
                :reduce="item => item.id"
                :options="categories_options"
                :readonly="!categories_options.length"
                label="name"
                placeholder="Category"
              />
            </app-form-group>
            <app-form-group label="Measurement" required>
              <app-select
                v-model="form.measurement"
                :reduce="item => item.id"
                :options="measurements_options"
                label="name"
                placeholder="Measurement"
                :error="$v.form.measurement.$error"
              />
              <template #error>
                <div v-if="$v.form.measurement.$dirty && !$v.form.measurement.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Name" required>
              <app-input
                v-model="form.name"
                placeholder="Name"
                :error="$v.form.name.$error"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="submit" type="submit">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getStorageDictsCategories,
  getStorageDictsItemTypes,
  getStorageDictsMeasurements,
  getStorageDictsSections,
  getStorageItems,
  patchStorageItems,
  postStorageItems
} from '@/http'
import debounce from 'lodash/debounce'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'StorageItems',
  data() {
    return {
      page_loaded: false,
      filter_search: '',
      filter_section: null,
      filter_type: null,
      section_options: [],
      categories_options: [],
      measurements_options: [],
      types_options: [],
      table: {
        items: [],
        columns: [
          'name',
          'item_type',
          'section',
          'category',
          'measurement',
          'weight',
          'controls',
        ],
        options: {
          headings: {
            'name': 'Name',
            'item_type': 'Type',
            'section': 'Section',
            'category': 'Category',
            'measurement': 'Measurement',
            'weight': 'Weight',
            'controls': '',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      form: {},
      item_to_change: null,
    }
  },
  validations: {
    form: {
      section: {required},
      item_type: {
        required: requiredIf(function () {
          return !this.item_to_change
        })
      },
      measurement: {required},
      name: {required},
    }
  },
  created() {
    Promise.all([
      getStorageDictsSections(),
      getStorageDictsMeasurements(),
      getStorageDictsItemTypes(),
      this.fetchItems(),
    ])
      .then(response => {
        this.section_options = response[0].data
        this.measurements_options = response[1].data
        this.types_options = response[2].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItems() {
      this.table_preloader = true
      const response = await getStorageItems({
        page: this.pagination.page,
        page_size: 40,
        search: this.filter_search,
        section: this.filter_section?.id,
        item_type: this.filter_type?.code,
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.filter_search = ''
      this.fetchItems()
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onShowModal(row) {
      this.item_to_change = row?.id
      if (!row) {
        this.form = {}
      } else {
        getStorageDictsCategories({section: row.section.id})
          .then(response => {
            this.categories_options = response.data
            this.form = {
              section: row.section.id,
              item_type: row.item_type?.code,
              measurement: row.measurement.id,
              category: row.category?.id,
              name: row.name,
              weight: row.weight,
            }
          })
      }
      this.$v.form.$reset()
      this.$modal.show('item_modal')
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.item_to_change) {
        patchStorageItems(this.item_to_change, this.form)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Data saved',
            })
            this.$modal.hide('item_modal')
          })
          .catch(() => {
            this.$refs.submit.preload = false
          })
      } else {
        postStorageItems(this.form)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Item created',
            })
            this.$modal.hide('item_modal')
          })
          .catch(() => {
            this.$refs.submit.preload = false
          })
      }
    },
    onChangeSection(e) {
      this.form.category = null
      this.categories_options = []
      if (e) {
        getStorageDictsCategories({section: e})
          .then(response => {
            this.categories_options = response.data
          })
      }
    },
    onItemSearch() {
      this.searchItem(this)
    },
    searchItem: debounce((vm) => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>
